import React, { useState, useEffect } from 'react';

function App() {
  const [showUnmuteHint, setShowUnmuteHint] = useState(true);

  useEffect(() => {
    // Hide the unmute hint after 8 seconds
    const timer = setTimeout(() => {
      setShowUnmuteHint(false);
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
      {/* Ambient gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/20 via-transparent to-purple-900/20 pointer-events-none"></div>
      
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat'
      }}></div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen py-16 px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <header className="text-center mb-12 max-w-4xl mx-auto animate-fadeIn">
          <div className="mb-3 inline-block">
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-blue-400 font-semibold px-4 py-1.5 border border-blue-400/30 rounded-full bg-blue-500/5 backdrop-blur-sm">
              Limited Access
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 break-keep leading-[1.15] tracking-tight">
            저절로 실행이 되는<br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              시스템 비밀특강
            </span>
          </h1>
          
          <div className="flex items-center justify-center gap-2 text-base sm:text-lg text-red-400 font-medium mb-2">
            <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <p>본 강의는 불시에 종료됩니다.</p>
          </div>
        </header>

        {/* Video Section */}
        <div className="w-full max-w-5xl mb-14 px-4">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 backdrop-blur-sm transition-transform hover:scale-[1.01] duration-500">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 rounded-2xl blur-xl opacity-20 animate-pulse"></div>
            
            {/* Audio guide hint overlay */}
            {showUnmuteHint && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 animate-fadeIn">
                <div className="flex items-center gap-2 bg-white/95 text-slate-900 px-6 py-3 rounded-full shadow-xl backdrop-blur-sm">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-sm sm:text-base">소리 켜고 시청하기</span>
                </div>
              </div>
            )}
            
            <div className="relative bg-black rounded-2xl overflow-hidden">
              <div className="relative pb-[56.25%] h-0">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://fast.wistia.net/embed/iframe/90iiyv8q0n?autoPlay=true&controlsVisibleOnLoad=true&playerColor=3b82f6&playbar=true&playButton=true&settingsControl=false&smallPlayButton=true&volumeControl=true&fullscreenButton=false&qualityControl=false&playbackRateControl=false&plugin[captions-v1][onByDefault]=false"
                  title="시스템 비밀특강"
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowTransparency={true}
                ></iframe>
              </div>
            </div>
          </div>
          
          {/* Audio guide text below video */}
          <div className="mt-4 text-center">
            <p className="text-slate-400 text-sm sm:text-base flex items-center justify-center gap-2">
              <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>소리를 켜고 강의를 들어보세요</span>
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center w-full max-w-2xl px-4 animate-slideUp">
          <a
            href="https://litt.ly/sdpower2/sale/VzqgeKh"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center justify-center"
          >
            {/* Button glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 rounded-full blur-lg opacity-70 group-hover:opacity-100 transition duration-500 animate-pulse"></div>
            
            {/* Button */}
            <div className="relative flex items-center justify-center bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg sm:text-xl md:text-2xl font-bold py-5 sm:py-6 px-8 sm:px-12 rounded-full shadow-xl transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl w-full">
              <span className="break-keep">나에게 딱맞는 일대일 실행 진단 알아보기</span>
              <svg className="ml-3 w-6 h-6 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </a>
          
          <p className="mt-6 text-slate-400 text-sm sm:text-base">
            지금 바로 나만의 실행 시스템을 진단받으세요
          </p>
        </div>

      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 1s ease-out 0.3s both;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );
}

export default App;
